// Geocodificación y ruteo 100% libres: Nominatim (OpenStreetMap) + OSRM.
// No requieren API key ni tarjeta.

export interface Coord {
  lat: number;
  lon: number;
}

export interface AddressSuggestion {
  id: string;
  /** Dirección completa (se coloca en el input al elegir). */
  label: string;
  /** Nombre corto del lugar o calle. */
  titulo: string;
  /** Colonia, municipio y estado. */
  detalle: string;
  coord: Coord;
}

export interface RouteResult {
  distanciaKm: number;
  duracionMin: number;
  /** Geometría de la ruta como pares [lat, lon]. */
  geometry: Array<[number, number]>;
}

const NOMINATIM = "https://nominatim.openstreetmap.org/search";
const OSRM = "https://router.project-osrm.org/route/v1/driving";
/** Área metropolitana de Guadalajara (Zapopan, Tlaquepaque, Tonalá, Tlajomulco). */
const VIEWBOX = "-103.45,20.75,-103.20,20.55";

interface NominatimItem {
  place_id: number;
  display_name: string;
  name?: string;
  lat: string;
  lon: string;
  address?: Record<string, string>;
}

async function nominatim(
  query: string,
  bounded: boolean,
  signal?: AbortSignal,
): Promise<NominatimItem[]> {
  const url =
    `${NOMINATIM}?format=jsonv2&addressdetails=1&limit=8&countrycodes=mx` +
    `&viewbox=${VIEWBOX}&bounded=${bounded ? 1 : 0}&accept-language=es&q=${encodeURIComponent(query)}`;
  const res = await fetch(url, {
    signal: signal ?? null,
    headers: { Accept: "application/json" },
  });
  if (!res.ok) return [];
  return (await res.json()) as NominatimItem[];
}

/** Autocompletado de direcciones (calle + número) priorizando Guadalajara / Zapopan. */
export async function searchAddresses(
  query: string,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) return [];

  // 1) Resultados exactos dentro del área metropolitana de Guadalajara.
  let json = await nominatim(q, true, signal);
  // 2) Si no hay coincidencias locales, se amplía a todo México.
  if (json.length === 0) json = await nominatim(q, false, signal);

  const ESTADOS: Record<string, string> = { Jalisco: "Jal." };
  const vistos = new Set<string>();
  const out: AddressSuggestion[] = [];

  for (const r of json) {
    const a = r.address ?? {};
    const partes = r.display_name.split(",").map((s) => s.trim());
    const calle = a["road"] || a["pedestrian"] || a["residential"] || "";
    const numero = a["house_number"] || "";
    const conNumero = [calle, numero].filter(Boolean).join(" ");
    const titulo = conNumero || r.name?.trim() || partes[0] || r.display_name;
    const colonia = a["neighbourhood"] || a["suburb"] || a["quarter"] || a["residential"] || "";
    const municipio = a["city"] || a["town"] || a["village"] || a["municipality"] || "";
    const estadoRaw = a["state"] || "";
    const estado = ESTADOS[estadoRaw] ?? estadoRaw;
    const cp = a["postcode"] ? `C.P. ${a["postcode"]}` : "";
    const detalle =
      [colonia && `Col. ${colonia}`, municipio, estado, cp].filter(Boolean).join(", ") ||
      partes.slice(1, 4).join(", ");
    const label = [titulo, detalle].filter(Boolean).join(", ") || r.display_name;
    if (vistos.has(label)) continue;
    vistos.add(label);
    out.push({
      id: String(r.place_id),
      label,
      titulo,
      detalle,
      coord: { lat: Number(r.lat), lon: Number(r.lon) },
    });
  }
  return out.slice(0, 6);
}


function haversineKm(a: Coord, b: Coord): number {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Ruta real de manejo con OSRM; si falla, línea recta (haversine). */
export async function getDrivingRoute(
  origen: Coord,
  destino: Coord,
  signal?: AbortSignal,
): Promise<RouteResult> {
  try {
    const url = `${OSRM}/${origen.lon},${origen.lat};${destino.lon},${destino.lat}?overview=full&geometries=geojson`;
    const res = await fetch(url, { signal: signal ?? null });
    if (res.ok) {
      const json = (await res.json()) as {
        routes?: Array<{
          distance: number;
          duration: number;
          geometry: { coordinates: Array<[number, number]> };
        }>;
      };
      const route = json.routes?.[0];
      if (route) {
        return {
          distanciaKm: Math.round((route.distance / 1000) * 10) / 10,
          duracionMin: Math.max(1, Math.round(route.duration / 60)),
          geometry: route.geometry.coordinates.map(([lon, lat]) => [lat, lon]),
        };
      }
    }
  } catch {
    // cae al respaldo
  }
  const km = Math.round(haversineKm(origen, destino) * 10) / 10;
  return {
    distanciaKm: km,
    duracionMin: Math.max(1, Math.round((km / 24) * 60)),
    geometry: [
      [origen.lat, origen.lon],
      [destino.lat, destino.lon],
    ],
  };
}
